// Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// React & React Hooks
import React, { useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

// Third-party libraries
import _ from "underscore";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// MUI components
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// MUI icons
import EastIcon from "@mui/icons-material/East";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WestIcon from "@mui/icons-material/West";

// Constants
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";

// Services / utilities
import { httpGet, httpPost } from "../../services/commonService";
import { getPhoneFieldValueFromRawNumber } from "../../shared-components/phoneHelper";

// Custom components
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";
import CustomPhoneField from "../../shared-components/form-controls/customPhoneField";
import CustomTextAreaField from "../../shared-components/form-controls/customTextAreaField";
import CustomTextField from "../../shared-components/form-controls/customTextField";
import UpdateUserInfo from "../../shared-components/updateUserInfoComponent/updateUserInfo";
import { selectUserDetail, userDetail } from "../../redux/slice/userSlice";

// Validators
import { nameValidator } from "../../shared-components/validator/fieldValidator";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {<Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const privateInformationTabs = [
  { id: 0, label: "PrivateInfo.User Information" },
  { id: 1, label: "PrivateInfo.Address Information" },
  { id: 2, label: "PrivateInfo.Shipping Information" },
];

let userData = [];

const PrivateInformation = () => {
  const authenticationState = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const [tabValue, setTabValue] = React.useState(0);  
  const [isPhoneFiledForceUpdate, setIsPhoneFiledForceUpdate] = useState(true);
  const [anchorElTab, setAnchorElTab] = useState(null);  
  const [visibleUserInfoModal, setVisibleUserInfoModal] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [isCompanyHide, setIsCompanyHide] = useState(true);
  const [isTaxIdDisabled, setIsTaxIdDisabled] = useState(false);
  const [isEliteOrApex, setIsEliteOrApex] = useState(false);
  const [isTradingViewUsernameIdDisabled, setIsTradingViewUsernameIdDisabled] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    countries: [],
    states: [],
    shippingStates: [],
    timezones: [],
    currencies: [],
    registrationTypes: [],
    phoneCountryCodeList: [],
  });

  const registrationTypeDefaultValue = [{ value: null, label: "Select  registration type" }];
  const timeZoneDefaultValue = [{ value: "", label: "Select time zone" }];
  const stateDefaultValue = [{ value: "", label: "Select state" }];
  const countryDefaultValue = [{ value: 1, label: "US" }];
  const currencyDefaultValue = [{ value: "", label: "Select currency" }];

  const methods = useForm({
    shouldUnregister: false,
    mode: "all",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      registrationType: null,
      company: "",
      mobile: "",
      address1: "",
      address2: "",
      city: "",
      state: null,
      country: null,
      currency: null,
      zipcode: "",
      shippingAddress1: "",
      shippingAddress2: "",
      shippingCity: "",
      shippingState: null,
      shippingCountry: null,
      shippingZipcode: "",
      shippingMobile: "",
      shippingPhone: "",
      timeZone: null,
      taxId: "",
      replicationSiteName: "",
      customerTypeID: null,
    },
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    trigger,
    formState: { errors },
  } = methods;

  const selectedCountry = watch("country");
  const selectedShippingCountry = watch("shippingCountry");

  useEffect(() => {
    getAllDropDownList();
    getRegistrationDropDownList();
  }, []);

  useEffect(() => {
    const latestPackageType = getLoggedUserDetails?.PurchasePackageDetails?.LatestPackageType || "";
    setIsEliteOrApex(
      latestPackageType.includes("ELITE") || latestPackageType.includes("PREMIER")
    );
  }, [getLoggedUserDetails]);


  const handleTabClick = (event) => {
    setAnchorElTab(event.currentTarget);
  };

  const handleTabClose = (lang) => {
    setAnchorElTab(null);
  };

  useEffect(() => {
    if (dropdownData.countries.length > 0 && dropdownData.registrationTypes.length > 0) {
      getUserDetailsById();
    }
  }, [dropdownData.countries, dropdownData.registrationTypes]);

  useEffect(() => {
    if (selectedCountry?.value) {
      getStateDropDownList(selectedCountry.value);
    }
  }, [selectedCountry?.value]);

  useEffect(() => {
    if (selectedShippingCountry?.value) {
      getShippingStateDropDownList(selectedShippingCountry.value);
    }
  }, [selectedShippingCountry?.value]);

  const getUserDetailsById = () => {
    httpGet(
      urlConstant.User.GetPrivateInfoById,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
    .then((response) => {
      const responseData = response.Value.Data;
      userData = responseData;

      const isNotCompanyType = responseData.RegistrationType !== dataConstant.RegistrationType.Company;
      setIsCompanyHide(isNotCompanyType);

      setIsTaxIdDisabled(responseData.TaxId !== null && responseData.TaxId !== "");
      setIsTradingViewUsernameIdDisabled(responseData.TradingViewUsernameRequestedOn !== null && responseData.TradingViewUsernameRequestedOn !== "");

      const findSelectedValue = (list, id, defaultValue) => {
        if (id === null || id === undefined) return defaultValue;
        const found = _.find(list, (item) => item.value === id);
        return found || defaultValue;
      };
      reset({
        firstName: userData.FirstName || "",
        lastName: userData.LastName || "",
        email: userData.Email || "",
        company: userData.Companyname || "",
        address1: userData.Address1 || "",
        address2: userData.Address2 || "",
        city: userData.City || "",
        state: null,
        taxId: userData.TaxId || "",
        zipcode: userData.Zipcode || "",
        shippingAddress1: userData.ShippingAddress1 || "",
        shippingAddress2: userData.ShippingAddress2 || "",
        shippingCity: userData.ShippingCity || "",
        shippingState: null,
        shippingZipcode: userData.ShippingZipCode || "",
        replicationSiteName: userData.ReplicationSiteName,
        customerTypeID: userData.CustomerTypeID,
        shippingMobile: getPhoneFieldValueFromRawNumber(userData.ShippingMobile) || "+1",
        shippingPhone: getPhoneFieldValueFromRawNumber(userData.ShippingPhone) || "+1",
        phone: getPhoneFieldValueFromRawNumber(userData.Phone) || "+1",
        mobile: getPhoneFieldValueFromRawNumber(userData.Mobile) || "+1",
        tradingViewUsername: userData.TradingViewUsername || "",
        tradingViewUsernameRequestedOn: userData.TradingViewUsernameRequestedOn || "",        
        registrationType: findSelectedValue(
          dropdownData.registrationTypes,
          userData.RegistrationType,
          registrationTypeDefaultValue
        ),
        country: findSelectedValue(
          dropdownData.countries,
          userData.CountryID,
          countryDefaultValue
        ),
        currency: findSelectedValue(
          dropdownData.currencies,
          userData.CurrencyID,
          currencyDefaultValue
        ),
        timeZone: findSelectedValue(
          dropdownData.timezones,
          userData.TimeZoneID,
          timeZoneDefaultValue
        ),
        shippingCountry: findSelectedValue(
          dropdownData.countries,
          userData.ShippingCountryID,
          countryDefaultValue
        ),
      });

      setIsPhoneFiledForceUpdate(!isPhoneFiledForceUpdate);
    })
    .catch((err) => {
      console.error(err);
    });
  };

  const handleCountryChange = (selectedOption) => {
    setValue("country", selectedOption);
    setValue("state", stateDefaultValue);
    if (selectedOption && selectedOption.value) {
      getStateDropDownList(selectedOption.value);
    }
  };

  const handleShippingCountryChange = (selectedOption) => {
    setValue("shippingCountry", selectedOption);
    setValue("shippingState", stateDefaultValue);
    if (selectedOption && selectedOption.value) {
      getShippingStateDropDownList(selectedOption.value);
    }
  };

  const handleRegistrationTypeChange = (selectedOption) => {
    setValue("registrationType", selectedOption);
    const isNotCompanyType = selectedOption?.value !== dataConstant.RegistrationType.Company;
    setIsCompanyHide(isNotCompanyType);
  };

  const handleTradingViewSubmit = async () => {
    const tradingUsername = methods.getValues("tradingViewUsername");
    
    if (!tradingUsername || tradingUsername.trim() === "") {
      showSnackbar("Please enter the TradingView username", "error");
      return;
    }
    const model = {
      AcctId: userData.AccID,
      TradingViewUsername: tradingUsername,
    };

    try {
      const response = await httpPost(
        urlConstant.User.SubmitTradingViewUsername,
        model,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );

      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        showSnackbar(response?.description, "success");
        setIsTradingViewUsernameIdDisabled(true);
      } else {
        showSnackbar(response?.error, "error");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formConfig = {
    firstNameParamValueId: {
      name: "firstName",
      label: t("PrivateInfo.First Name"),
      type: "text",
      required: true,
      maxLength: 100,
      validation: {
        validate: nameValidator("PrivateInfo.First Name"),
      },
    },
    lastNameParamValueId: {
      name: "lastName",
      label: t("PrivateInfo.Last Name"),
      type: "text",
      required: true,
      maxLength: 100,
      validation: {
        validate: nameValidator("PrivateInfo.Last Name"),
      },
    },
    emailParamValueId: {
      name: "email",
      label: t("PrivateInfo.Email Address"),
      type: "email",
      maxLength: 100,
      required: true,
      isDisabled: true,
    },
    registrationTypeParamValueId: {
      name: "registrationType",
      label: t("PrivateInfo.Registration Type"),
      type: "select",
      required: true,
      dropDownList: dropdownData.registrationTypes,
      customOnChange: handleRegistrationTypeChange,
    },
    companyParamValueId: {
      name: "company",
      label: t("PrivateInfo.Company"),
      type: "text",
      maxLength: 100,
      required: !isCompanyHide,
    },
    address1ParamValueId: {
      name: "address1",
      label: t("PrivateInfo.Address Line 1"),
      type: "text",
      maxLength: 200,
      required: true,
    },
    address2ParamValueId: {
      name: "address2",
      label: t("PrivateInfo.Address Line 2"),
      type: "text",
      maxLength: 200,
    },
    cityParamValueId: {
      name: "city",
      label: t("PrivateInfo.City"),
      type: "text",
      maxLength: 100,
      required: true,
    },
    stateParamValueId: {
      name: "state",
      label: t("PrivateInfo.State"),
      type: "select",
      required: true,
      dropDownList: dropdownData.states,
    },
    countryParamValueId: {
      name: "country",
      label: t("PrivateInfo.Country"),
      type: "select",
      required: true,
      dropDownList: dropdownData.countries,
      customOnChange: handleCountryChange,
    },
    currencyParamValueId: {
      name: "currency",
      label: t("PrivateInfo.Currency"),
      type: "select",
      required: true,
      dropDownList: dropdownData.currencies,
    },
    zipcodeParamValueId: {
      name: "zipcode",
      label: t("PrivateInfo.Zip Code"),
      type: "text",
      maxLength: 25,
      required: true,
    },
    shippingAddress1ParamValueId: {
      name: "shippingAddress1",
      label: t("PrivateInfo.Shipping Address 1"),
      type: "text",
      maxLength: 200,
    },
    shippingAddress2ParamValueId: {
      name: "shippingAddress2",
      label: t("PrivateInfo.Shipping Address 2"),
      type: "text",
      maxLength: 200,
    },
    shippingCityParamValueId: {
      name: "shippingCity",
      label: t("PrivateInfo.Shipping City"),
      type: "text",
      maxLength: 100,
    },
    shippingStateParamValueId: {
      name: "shippingState",
      label: t("PrivateInfo.Shipping State"),
      type: "select",
      dropDownList: dropdownData.shippingStates,
    },
    shippingCountryParamValueId: {
      name: "shippingCountry",
      label: t("PrivateInfo.Shipping Country"),
      type: "select",
      dropDownList: dropdownData.countries,
      customOnChange: handleShippingCountryChange,
    },
    shippingZipcodeParamValueId: {
      name: "shippingZipcode",
      label: t("PrivateInfo.Shipping Zip Code"),
      type: "text",
      maxLength: 25,
    },
    timeZoneParamValueId: {
      name: "timeZone",
      label: t("PrivateInfo.Time Zone"),
      type: "select",
      required: true,
      dropDownList: dropdownData.timezones,
    },
    taxIdParamValueId: {
      name: "taxId",
      label: t("PrivateInfo.Taxpayer ID"),
      type: "text",
      maxLength: 20,
      required: true,
      isDisabled: isTaxIdDisabled,
    },
    tradingViewUsernameParamValueId: {
      name: "tradingViewUsername",
      label: t("PrivateInfo.TradingView Username"),
      type: "text",
      maxLength: 100,
      isDisabled: isTradingViewUsernameIdDisabled,
    },
  };

  const getAllDropDownList = () => {
    const dropDownTypes = [
      dataConstant.AllDropDown.CoutryDropDown,
      dataConstant.AllDropDown.TimeZoneDropDown,
      dataConstant.AllDropDown.CurrencyDropDown,
    ];
    const model = {
      DropDownTypes: dropDownTypes.join(),
    };
    httpPost(
      urlConstant.Common.GetAllDropDownData,
      model,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
    .then((response) => {
      const countryList = response.Value.CoutryDropDownResult.Data;
      const timezoneList = response.Value.TimeZoneDropDownResult.Data;
      const currencyList = response.Value.CurrencyDropDownResult.Data;
      
      setDropdownData((prev) => ({
        ...prev,
        countries: countryList,
        timezones: timezoneList,
        currencies: currencyList,
        phoneCountryCodeList: countryList
          .filter((item) => !item.isDisabled)
          .map((item) => item.countryCode?.toLowerCase?.() || ""),
      }));

      setIsPhoneFiledForceUpdate(!isPhoneFiledForceUpdate);
    })
    .catch((err) => {
      console.error(err);
    });
  };

  const getRegistrationDropDownList = () => {
    httpGet(
      urlConstant.Common.GetRegistrationTypeList,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
    .then((response) => {
      const registrationTypeList = response.Value.Data;

      setDropdownData((prev) => ({
        ...prev,
        registrationTypes: registrationTypeList,
      }));
    })
    .catch((err) => {
      console.error(err);
    });
  };

  const getStateDropDownList = (countryId) => {
    if (countryId !== null && countryId !== undefined) {
      httpGet(
        `${urlConstant.Common.GetStateList}?CountryId=${countryId}`,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          const stateList = response.Value.Data;

          setDropdownData((prev) => ({
            ...prev,
            states: stateList,
          }));
          if (
            userData &&
            userData.StateID !== null &&
            userData.StateID !== undefined
          ) {
            const stateValue =
              _.find(stateList, (i) => i.value === userData.StateID) ||
              stateDefaultValue;
            setValue("state", stateValue);
          } else {
            setValue("state", stateDefaultValue);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const getShippingStateDropDownList = (countryId) => {
    if (countryId !== null && countryId !== undefined) {
      httpGet(
        `${urlConstant.Common.GetStateList}?CountryId=${countryId}`,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          const stateList = response.Value.Data;
          setDropdownData((prev) => ({
            ...prev,
            shippingStates: stateList,
          }));
          if (
            userData &&
            userData.ShippingStateID !== null &&
            userData.ShippingStateID !== undefined
          ) {
            const stateValue =
              _.find(stateList, (i) => i.value === userData.ShippingStateID) ||
              stateDefaultValue;
            setValue("shippingState", stateValue);
          } else {
            setValue("shippingState", stateDefaultValue);
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const TAB_FIELDS = {
    0: [
      "firstName",
      "lastName",
      "email",
      "phone",
      "timeZone",
      "currency",
      "registrationType",
      "company",
      "taxId"
    ],
    1: ["address1", "city", "state", "country", "zipcode"],
    2: [
      "shippingAddress1",
      "shippingCity",
      "shippingState",
      "shippingCountry",
      "shippingZipcode",
      "shippingPhone",
      "shippingMobile",
    ],
  };

  const getFieldsToValidate = (tabIndex) => TAB_FIELDS[tabIndex] || [];

  const validateCurrentTabFields = async (tabIndex) => {
    const fields = getFieldsToValidate(tabIndex);
    return await trigger(fields);
  };

  const handleTabNavigation = async (newTabIndex) => {
    const isValid = await validateCurrentTabFields(tabValue);
    if (isValid) {
      setTabValue(newTabIndex);
    }
  };

  const handleTabChange = (event, newValue) => {
    handleTabNavigation(newValue, true);
  };

  const handleNextTab = async (nextTabIndex) => {
    await handleTabNavigation(nextTabIndex, false);
  };

  const getFirstErrorTabIndex = () => {
    const tabIndexes = Object.keys(TAB_FIELDS);
    for (let idx of tabIndexes) {
      const fields = TAB_FIELDS[idx];
      if (fields.some((field) => errors[field])) {
        return Number(idx);
      }
    }
    return null;
  };

  const handleSaveClick = async () => {
    const isValid = await trigger();
    if (!isValid) {
      const errorTab = getFirstErrorTabIndex();
      if (errorTab !== null) {
        setTabValue(errorTab);
        await trigger(TAB_FIELDS[errorTab]);
      }
      return;
    }
  };

  const onSubmit = async (data) => {
    try {
      const model = {
        FirstName: data.firstName,
        LastName: data.lastName,
        Phone: data.phone.fullNumber,
        RegistrationType: data.registrationType ? data.registrationType.value : null,
        Companyname: data.company,
        Mobile: data.mobile.fullNumber,
        Address1: data.address1,
        Address2: data.address2,
        City: data.city,
        StateID: data.state ? data.state.value : null,
        CountryID: data.country ? data.country.value : null,
        CurrencyID: data.currency ? data.currency.value : null,
        Zipcode: data.zipcode,
        ShippingAddress1: data.shippingAddress1,
        ShippingAddress2: data.shippingAddress2,
        ShippingCity: data.shippingCity,
        ShippingStateID: data.shippingState ? data.shippingState.value : null,
        ShippingCountryID: data.shippingCountry ? data.shippingCountry.value : null,
        ShippingZipcode: data.shippingZipcode,
        ShippingMobile: data.shippingMobile.fullNumber,
        ShippingPhone: data.shippingPhone.fullNumber,
        TimeZoneID: data.timeZone ? data.timeZone.value : null,
        ReplicationSiteName: data.replicationSiteName,
        CustomerTypeID: data.customerTypeID,
        TaxId: data.taxId,
        TradingViewUsername: data.tradingViewUsername,
      };
      const response = await httpPost(
        urlConstant.User.EditPrivateInfo,
        model,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        showSnackbar(response.description, "success");
        const updatedUserDetails = {
          ...getLoggedUserDetails,
          UserAccountDetails: {
            ...getLoggedUserDetails.UserAccountDetails,
            PvtFirstName: data.firstName,
            PvtLastName: data.lastName,
            PvtAcctFullName: `${data.firstName} ${data.lastName}`
          },
        };
        dispatch(userDetail({ userDetail: updatedUserDetails }));

        setValue("taxId", data.taxId.slice(-4));
        setIsTaxIdDisabled(true);

        setTabValue(0);
      } else {
        showSnackbar(response?.description || "Update failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const showUserInfoModal = () => {
    setVisibleUserInfoModal(true);
  };

  const closeUserInfoModal = () => {
    setVisibleUserInfoModal(false);
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("My Account")}
        </Typography>
        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              border: "1px solid var(--headerbg)",
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Alert
                sx={(theme) => ({
                  background: theme.palette.common.errorbg,
                  color: theme.palette.common.errortext,
                })}
                variant="filled"
                severity="info"
              >
                {t("PrivateInfo.Information Entered")}
              </Alert>
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("PrivateInfo.Private Information")}
              </Typography>
              <Stack className="tabs-common">
                <Box sx={{ px: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="basic tabs example"
                  >
                    <Tab
                      label={t("PrivateInfo.User Information")}
                      {...a11yProps(0)}
                    />
                    <Tab
                      label={t("PrivateInfo.Address Information")}
                      {...a11yProps(1)}
                    />
                    <Tab
                      label={t("PrivateInfo.Shipping Information")}
                      {...a11yProps(2)}
                    />
                  </Tabs>
                </Box>
                <Stack>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <CustomTabPanel value={tabValue} index={0}>
                      <Grid
                        container
                        spacing={3}
                        sx={{ flexGrow: 1, width: "100%" }}
                      >
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Stack
                            direction={"row"}
                            flexWrap={"wrap"}
                            justifyContent={"space-between"}
                            mb={2}
                            alignContent={"center"}
                            sx={{ display: { xs: "flex", sm: "none" } }}
                          >
                            <Typography
                              color="secondary.light"
                              variant="h4"
                              fontWeight={600}
                            >
                              {t("PrivateInfo.User Information")}
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleTabClick}
                              sx={{
                                px: 0.8,
                                py: 1,
                              }}
                            >
                              <MoreVertIcon />
                            </Button>
                          </Stack>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "flex-start", sm: "flex-end" }}
                            gap={1.5}
                            flexWrap={{ xs: "wrap", sm: "wrap" }}
                          >
                            <Stack
                              minWidth={{ xs: "auto", sm: "250px" }}
                              width={"100%"}
                              sx={{ flex: { xs: 1, lg: 1 } }}
                            >
                              <CustomTextField
                                methods={methods}
                                properties={formConfig.emailParamValueId}
                              />
                            </Stack>
                            <Stack
                              sx={{
                                flex: { xs: 1, lg: "none" },
                                width: { xs: "100%", sm: "auto" },
                              }}
                            >
                              <Button
                                color="secondary"
                                onClick={showUserInfoModal}
                                sx={{
                                  whiteSpace: "pre",
                                  px: 4,
                                  py: 1.65,
                                  width: {
                                    xs: "100%",
                                    sm: "auto",
                                  },
                                  flexGrow: "initial",
                                }}
                                variant="contained"
                              >
                                {t("PrivateInfo.Change Login Email")}
                              </Button>
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.timeZoneParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.firstNameParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.lastNameParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomPhoneField
                            isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                            control={control}
                            name="mobile"
                            label={t("PrivateInfo.Phone Number Alt")}
                            isAltPhone={true}
                            countryList={dropdownData.phoneCountryCodeList}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomPhoneField
                            isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                            control={control}
                            name="phone"
                            label={t("PrivateInfo.Phone Number")}
                            required={true}
                            countryList={dropdownData.phoneCountryCodeList}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.taxIdParamValueId}
                          />
                          {isTaxIdDisabled && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#6c757d" }}
                            >
                              {t("PrivateInfo.To update Taxpayer Information")}
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.currencyParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.registrationTypeParamValueId}
                          />
                        </Grid>
                        {!isCompanyHide && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <CustomTextField
                              methods={methods}
                              properties={formConfig.companyParamValueId}
                            />
                          </Grid>
                        )}
                        {isEliteOrApex && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              alignItems={{ xs: "flex-start", sm: isTradingViewUsernameIdDisabled ? "center" : "flex-end", }}
                              gap={1.5}
                              flexWrap={{ xs: "wrap", sm: "wrap" }}
                            >
                              <Stack
                                minWidth={{ xs: "auto", sm: "250px" }}
                                width={"100%"}
                                sx={{ flex: { xs: 1, lg: 1 } }}
                              >
                                <CustomTextField
                                  methods={methods}
                                  properties={formConfig.tradingViewUsernameParamValueId}
                                />
                                {isTradingViewUsernameIdDisabled && (                            
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#6c757d" }}
                                  >
                                    {t("PrivateInfo.To update TradingView Username Information")}
                                  </Typography> 
                                )}                           
                              </Stack>
                              <Stack
                                sx={{
                                  flex: { xs: 1, lg: "none" },
                                  width: { xs: "100%", sm: "auto" },
                                }}
                              >
                                <Button
                                  color="primary"
                                  onClick={handleTradingViewSubmit}
                                  disabled={isTradingViewUsernameIdDisabled}
                                  sx={{
                                    whiteSpace: "pre",
                                    px: 4,
                                    py: 1.65,
                                    width: {
                                      xs: "100%",
                                      sm: "auto",
                                    },
                                    flexGrow: "initial",
                                  }}
                                  variant="contained"
                                >
                                  {t("Common.Submit")}
                                </Button>
                              </Stack>
                            </Stack>
                          </Grid>
                        )}
                        <Stack
                          width={"100%"}
                          alignItems={{ xs: "flex-end", sm: "center" }}
                        >
                          <Button
                            variant="contained"
                            endIcon={<EastIcon />}
                            onClick={() => handleNextTab(1)}
                            sx={{ px: 3, gap: 1 }}
                          >
                            {t("Common.Next")}
                          </Button>
                        </Stack>
                      </Grid>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                      <Grid
                        container
                        spacing={3}
                        sx={{ flexGrow: 1, width: "100%" }}
                      >
                        <Grid size={{ xs: 12 }}>
                          <Stack
                            direction={"row"}
                            flexWrap={"wrap"}
                            justifyContent={"space-between"}
                            mb={2}
                            alignContent={"center"}
                            sx={{ display: { xs: "flex", sm: "none" } }}
                          >
                            <Typography
                              color="secondary.light"
                              variant="h4"
                              fontWeight={600}
                            >
                              {t("PrivateInfo.Address Information")}
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleTabClick}
                              sx={{
                                px: 0.8,
                                py: 1,
                              }}
                            >
                              <MoreVertIcon />
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextAreaField
                            methods={methods}
                            properties={formConfig.address1ParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextAreaField
                            methods={methods}
                            properties={formConfig.address2ParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.countryParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.stateParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.cityParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.zipcodeParamValueId}
                          />
                        </Grid>
                        <Stack
                          width={"100%"}
                          alignItems={"center"}
                          flexWrap={"wrap"}
                          justifyContent={{ xs: "space-between", sm: "center" }}
                          gap={2}
                          direction={"row"}
                        >
                          <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<WestIcon />}
                            onClick={() => handleNextTab(0)}
                            sx={{ px: 3, gap: 1 }}
                          >
                            {t("Common.Prev")}
                          </Button>
                          <Button
                            variant="contained"
                            endIcon={<EastIcon />}
                            onClick={() => handleNextTab(2)}
                            sx={{ px: 3, gap: 1 }}
                          >
                            {t("Common.Next")}
                          </Button>
                        </Stack>
                      </Grid>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={2}>
                      <Grid
                        container
                        spacing={3}
                        sx={{ flexGrow: 1, width: "100%" }}
                      >
                        <Grid size={{ xs: 12 }}>
                          <Stack
                            direction={"row"}
                            flexWrap={"wrap"}
                            justifyContent={"space-between"}
                            mb={2}
                            alignContent={"center"}
                            sx={{ display: { xs: "flex", sm: "none" } }}
                          >
                            <Typography
                              color="secondary.light"
                              variant="h4"
                              fontWeight={600}
                            >
                              {t("PrivateInfo.Shipping Information")}
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handleTabClick}
                              sx={{
                                px: 0.8,
                                py: 1,
                              }}
                            >
                              <MoreVertIcon />
                            </Button>
                          </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextAreaField
                            methods={methods}
                            properties={formConfig.shippingAddress1ParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextAreaField
                            methods={methods}
                            properties={formConfig.shippingAddress2ParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.shippingCountryParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomAutoCompleteDropdown
                            methods={methods}
                            properties={formConfig.shippingStateParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.shippingCityParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomTextField
                            methods={methods}
                            properties={formConfig.shippingZipcodeParamValueId}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomPhoneField
                            isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                            control={control}
                            name="shippingPhone"
                            label={t("PrivateInfo.Shipping Phone Number")}
                            isAltPhone={true}
                            countryList={dropdownData.phoneCountryCodeList}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CustomPhoneField
                            isPhoneFiledForceUpdate={isPhoneFiledForceUpdate}
                            control={control}
                            name="shippingMobile"
                            label={t("PrivateInfo.Shipping Mobile Number")}
                            isAltPhone={true}
                            countryList={dropdownData.phoneCountryCodeList}
                          />
                        </Grid>
                        <Stack
                          width={"100%"}
                          alignItems={"center"}
                          justifyContent={"center"}
                          gap={2}
                          direction={"row"}
                        >
                          <Button
                            variant="outlined"
                            color="secondary"
                            sx={{ px: 3, gap: 1 }}
                            startIcon={<WestIcon />}
                            onClick={() => handleNextTab(1)}
                          >
                            {t("Common.Prev")}
                          </Button>
                        </Stack>
                      </Grid>
                    </CustomTabPanel>

                    <Stack
                      width={"100%"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      mt={2}
                    >
                      {" "}
                      <Button
                        type="submit"
                        onClick={handleSaveClick}
                        sx={{ px: 3, width: { xs: "100%", sm: "auto" } }}
                        variant="contained"
                      >
                        {t("Common.Save")}
                      </Button>
                    </Stack>
                  </form>
                </Stack>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>

      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
      
      <UpdateUserInfo
        modalVisible={visibleUserInfoModal}
        userId={userData.UserId}
        cancelCallbackMethod={closeUserInfoModal}
        initialEmail={methods.getValues().email}
      />

      <Menu
        anchorEl={anchorElTab}
        open={Boolean(anchorElTab)}
        onClose={() => handleTabClose()}
      >
        {privateInformationTabs?.map((item) => (
          <MenuItem
            key={item.id}
            selected={item.id === tabValue}
            onClick={(e) => {
              handleTabClose();
              handleTabChange(e, item.id);
            }}
          >
            {t(item.label)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
export default PrivateInformation;